import React, { useEffect, useRef, useState, useMemo } from 'react';
import './MultiCheckboxSelect.css';
import '../Select/Select.css';

const MultiCheckboxSelect = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Selecione',
  showSelectAll = true,
  selectAllLabel = 'Selecionar todas',
  disabled = false,
  error,
}) => {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef(null);

  const cleanOptions = useMemo(
    () => (options || []).filter((o) => o && typeof o.value !== 'undefined' && String(o.value).trim() !== ''),
    [options]
  );

  const allValues = useMemo(() => cleanOptions.map((o) => o.value), [cleanOptions]);
  const isAllSelected = allValues.length > 0 && allValues.every((v) => selectedValues.includes(v));

  const labelResumo = useMemo(() => {
    if (!selectedValues || selectedValues.length === 0) return placeholder;
    const mapa = new Map(cleanOptions.map((o) => [o.value, o.label]));
    const labels = selectedValues
      .map((v) => mapa.get(v))
      .filter(Boolean);
    return labels.length ? labels.join(', ') : placeholder;
  }, [selectedValues, cleanOptions, placeholder]);

  // Fechar ao clicar fora
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!expanded) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [expanded]);

  const toggleOption = (value) => {
    if (disabled) return;
    const has = selectedValues.includes(value);
    const next = has ? selectedValues.filter((v) => v !== value) : [...selectedValues, value];
    onChange?.(next);
  };

  const toggleAll = () => {
    if (disabled) return;
    const valoresSemOutros = allValues.filter(v => v !== 'outros');
    const next = isAllSelected
      ? []
      : [...valoresSemOutros];
    onChange?.(next);
  };


  const handleOpen = () => {
    if (!disabled) setExpanded((v) => !v);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setExpanded((v) => !v);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`multiselect ${error ? 'error' : ''}`}
      aria-disabled={disabled ? 'true' : 'false'}
    >
      <div
        className={`selectBox ${disabled ? 'is-disabled' : ''}`}
        tabIndex={disabled ? -1 : 0}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={expanded}
      >
        <select
          className="form-control mcs-input"
          aria-label="Lógica de personalização selecionada"
          value=""
          title={labelResumo}
          disabled={disabled}
          onChange={() => { }}
        >
          <option value="">{labelResumo}</option>
        </select>
        <div className="overSelect" />
      </div>

      {expanded && (
        <div
          className="checkboxes"
          role="listbox"
          aria-multiselectable="true"
          onClick={(e) => e.stopPropagation()}
        >
          {showSelectAll && (
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
              />
              {selectAllLabel}
            </label>
          )}

          {cleanOptions.map((opt) => (
            <label key={opt.value} className="checkbox-option">
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}

      {error && <p className="error-message">{error}</p>} 
    </div>
  );
};

export default MultiCheckboxSelect;
